import {
    Button,
    CircularProgress,
    Container,
    Stack,
    TextField,
    Typography,
    Box
} from '@mui/material';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useLogin, useNotify } from 'react-admin';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Navigate, Link } from 'react-router-dom';
import { useConfigurationContext, useThemeLogo } from '../../root/ConfigurationContext';
import { LoginData } from '../../types/types';
import { getAppStatus } from '../../providers/firebase';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { FooterLinks } from '../../components/FooterLinks';

export const LoginPage = () => {
    const { title } = useConfigurationContext();
    const notify = useNotify();
    const login = useLogin();
    const navigate = useNavigate();

    // Fetch App Status
    const { data: appStatus, error, isPending } = useQuery({
        queryKey: ['app_status'],
        queryFn: getAppStatus,
    });

    const { register, handleSubmit, formState: { isValid } } = useForm<LoginData>({
        mode: 'onChange',
    });

    const { mutate, isPending: isLoginPending } = useMutation({
        mutationKey: ['login'],
        mutationFn: async (data: LoginData) => {
            const result = await login(data);
        },
        onSuccess: () => {
            notify("Login successful!");
            navigate('/');
        },
        onError: (error: any) => {
            console.error("Login failed:", error.message || error); // Debug log
            notify(error.message || "Login failed. Please try again.", { type: 'error' });
        },
    });

    const onSubmit: SubmitHandler<LoginData> = async data => {
        mutate(data);
    };

    // For logo
    const logo = useThemeLogo();

    // Keep this below every hook: the status arrives after the first render, and
    // returning earlier changes the hook count between renders.
    if (appStatus?.app_locked) {
        return <Navigate to="/maintenance" />;
    }

    return (
        <Stack sx={{ height: '100dvh', p: 2 }}>
            <Stack direction="row" alignItems="center" gap={1}>
                <Link to="/">
                    <img src={logo} alt={title} width={200} style={{ cursor: 'pointer' }} />
                </Link>
            </Stack>
            <Stack sx={{ height: '100%' }}>
                <Container
                    maxWidth="xs"
                    sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        gap: 1,
                    }}
                >
                    <Typography variant="body1" gutterBottom>
                        Enter your credentials to sign in.
                    </Typography>
                    <form
                        onSubmit={handleSubmit(onSubmit)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleSubmit(onSubmit)();
                            }
                        }}
                    >
                        <TextField
                            {...register('username', { required: true })}
                            label="Email"
                            type="email"
                            variant="outlined"
                            fullWidth
                            required
                        />
                        <TextField
                            {...register('password', { required: true })}
                            label="Password"
                            type="password"
                            variant="outlined"
                            fullWidth
                            required
                        />
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mt={2}>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={!isValid || isLoginPending}
                                fullWidth
                            >
                                {isLoginPending ? (
                                    <CircularProgress size={24} />
                                ) : (
                                    'Sign In'
                                )}
                            </Button>
                        </Stack>
                    </form>
                </Container>
            </Stack>
            <FooterLinks />
        </Stack>
    );
};

LoginPage.path = '/login';
